/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Button,
  Avatar,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Block as BlockIcon,
  Email as EmailIcon,
  CalendarToday as CalendarTodayIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Domain as DomainIcon,
  CardMembership as PlanIcon,
  Phone as PhoneIcon,
  Group as GroupIcon,
  Apartment as ApartmentIcon,
} from "@mui/icons-material";
import api from "services/api";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";

const Dashboard = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // State
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [summary, setSummary] = useState({
    total_companies: 0,
    active_companies: 0,
    trial_companies: 0,
    suspended_companies: 0,
    total_employees: 0,
    total_active_employees: 0,
  });

  // Filter & Search
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Details Modal
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);

  // Status Change State
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  // Fetch Companies
  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (statusFilter !== "all") params.status = statusFilter;

      const res = await api.get("/v1/api/superadmin/companies", { params });
      if (res?.data?.meta?.status || res?.meta?.status) {
        const data = res.data?.data || res.data || {};
        setCompanies(data.companies || []);
        if (data.summary) {
          setSummary(data.summary);
        }
      } else {
        showErrorToast("Failed to load companies data");
      }
    } catch (err) {
      console.error("Error fetching superadmin companies:", err);
      showErrorToast("Error loading companies list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [statusFilter]);

  // Handle Search submit
  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      fetchCompanies();
    }
  };

  // Open Details Modal
  const handleViewDetails = async (company) => {
    setSelectedCompany(company);
    setNewStatus(company.status);
    setDetailsModalOpen(true);
    setDetailsLoading(true);

    try {
      const res = await api.get(`/v1/api/superadmin/companies/${company.id}`);
      if (res?.data?.meta?.status || res?.meta?.status) {
        setCompanyDetails(res.data?.data || res.data || null);
      }
    } catch (err) {
      console.error("Error fetching company details:", err);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Update Status
  const handleUpdateStatus = async () => {
    if (!selectedCompany || !newStatus || newStatus === selectedCompany.status) return;

    try {
      setUpdatingStatus(true);
      const res = await api.put(`/v1/api/superadmin/companies/${selectedCompany.id}/status`, {
        status: newStatus,
      });

      if (res?.data?.meta?.status || res?.meta?.status) {
        showSuccessToast(`Company status updated to ${newStatus}`);
        setDetailsModalOpen(false);
        fetchCompanies();
      } else {
        showErrorToast("Failed to update status");
      }
    } catch (err) {
      console.error("Status update error:", err);
      showErrorToast("Error updating company status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Status Chip Renderer
  const renderStatusChip = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "active") {
      return (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
          label="Active"
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            bgcolor: isDark ? "rgba(16, 185, 129, 0.15)" : "#ecfdf5",
            color: isDark ? "#34d399" : "#059669",
            border: `1px solid ${isDark ? "rgba(16, 185, 129, 0.3)" : "#a7f3d0"}`,
          }}
        />
      );
    }
    if (s === "trial") {
      return (
        <Chip
          icon={<HourglassEmptyIcon sx={{ fontSize: "14px !important" }} />}
          label="Trial"
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            bgcolor: isDark ? "rgba(245, 158, 11, 0.15)" : "#fffbeb",
            color: isDark ? "#fbbf24" : "#d97706",
            border: `1px solid ${isDark ? "rgba(245, 158, 11, 0.3)" : "#fde68a"}`,
          }}
        />
      );
    }
    return (
      <Chip
        icon={<BlockIcon sx={{ fontSize: "14px !important" }} />}
        label="Suspended"
        size="small"
        sx={{
          fontWeight: 600,
          fontSize: "0.75rem",
          bgcolor: isDark ? "rgba(239, 68, 68, 0.15)" : "#fef2f2",
          color: isDark ? "#f87171" : "#dc2626",
          border: `1px solid ${isDark ? "rgba(239, 68, 68, 0.3)" : "#fecaca"}`,
        }}
      />
    );
  };

  // Filtered & Paginated Companies
  const displayedCompanies = useMemo(() => {
    return companies.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [companies, page, rowsPerPage]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        p: { xs: 2, md: 3 },
        transition: "background-color 0.3s",
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <motion.div initial={{ opacity: 0, y: -15 }} animate={{ opacity: 1, y: 0 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
          >
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "-0.025em",
                  color: "text.primary",
                }}
              >
                Companies Overview
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
                Monitor registered companies and their employee engagement metrics
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchCompanies}
              disabled={loading}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                borderColor: isDark ? "rgba(255,255,255,0.15)" : "#e5e7eb",
                color: "text.primary",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f9fafb",
                },
              }}
            >
              Refresh
            </Button>
          </Stack>
        </motion.div>
      </Box>

      {/* Summary Stat Cards */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {/* Card 1: Total Companies */}
        <Grid item xs={12} sm={6} md={3}>
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: "16px",
                border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                bgcolor: isDark ? "#0f172a" : "#ffffff",
                boxShadow: isDark
                  ? "0 4px 20px -2px rgba(0,0,0,0.5)"
                  : "0 4px 20px -2px rgba(0,0,0,0.04)",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase" }}>
                      Total Companies
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: "text.primary" }}>
                      {summary.total_companies || 0}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: isDark ? "rgba(59, 130, 246, 0.15)" : "#eff6ff",
                      color: "#3b82f6",
                    }}
                  >
                    <BusinessIcon />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Card 2: Active Companies */}
        <Grid item xs={12} sm={6} md={3}>
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: "16px",
                border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                bgcolor: isDark ? "#0f172a" : "#ffffff",
                boxShadow: isDark
                  ? "0 4px 20px -2px rgba(0,0,0,0.5)"
                  : "0 4px 20px -2px rgba(0,0,0,0.04)",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase" }}>
                      Active
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: "#10b981" }}>
                      {summary.active_companies || 0}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: isDark ? "rgba(16, 185, 129, 0.15)" : "#ecfdf5",
                      color: "#10b981",
                    }}
                  >
                    <CheckCircleIcon />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Card 3: On Trial */}
        <Grid item xs={12} sm={6} md={3}>
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: "16px",
                border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                bgcolor: isDark ? "#0f172a" : "#ffffff",
                boxShadow: isDark
                  ? "0 4px 20px -2px rgba(0,0,0,0.5)"
                  : "0 4px 20px -2px rgba(0,0,0,0.04)",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase" }}>
                      Trial Period
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: "#f59e0b" }}>
                      {summary.trial_companies || 0}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: isDark ? "rgba(245, 158, 11, 0.15)" : "#fffbeb",
                      color: "#f59e0b",
                    }}
                  >
                    <HourglassEmptyIcon />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Card 4: Total Employees */}
        <Grid item xs={12} sm={6} md={3}>
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: "16px",
                border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                bgcolor: isDark ? "#0f172a" : "#ffffff",
                boxShadow: isDark
                  ? "0 4px 20px -2px rgba(0,0,0,0.5)"
                  : "0 4px 20px -2px rgba(0,0,0,0.04)",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase" }}>
                      Total Employees
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: "#6366f1" }}>
                      {summary.total_employees || 0}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "#eef2ff",
                      color: "#6366f1",
                    }}
                  >
                    <PeopleIcon />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Main Table Paper */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
            bgcolor: isDark ? "#0f172a" : "#ffffff",
            overflow: "hidden",
            boxShadow: isDark
              ? "0 10px 25px -5px rgba(0,0,0,0.4)"
              : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
          }}
        >
          {/* Table Filters & Search Bar */}
          <Box
            sx={{
              p: 2.5,
              borderBottom: `1px solid ${isDark ? "#1e293b" : "#f1f5f9"}`,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
            >
              {/* Search Field */}
              <TextField
                placeholder="Search by company, subdomain or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                size="small"
                sx={{
                  width: { xs: "100%", sm: 340 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Status Filter */}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="status-filter-label">Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{
                      borderRadius: "10px",
                      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                    }}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="trial">Trial</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  onClick={fetchCompanies}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2.5,
                    boxShadow: "none",
                  }}
                >
                  Search
                </Button>
              </Stack>
            </Stack>
          </Box>

          {/* Table */}
          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
              <CircularProgress size={36} />
              <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                Loading companies...
              </Typography>
            </Box>
          ) : companies.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <DomainIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: "text.secondary" }}>
                No Companies Found
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                Try adjusting your search query or filters.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 850 }}>
                <TableHead
                  sx={{
                    bgcolor: isDark ? "#1e293b" : "#f8fafc",
                  }}
                >
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary", width: 60 }}>
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>
                      COMPANY
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>
                      CONTACT / OWNER
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>
                      PLAN
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>
                      TOTAL EMPLOYEES
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>
                      STATUS
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>
                      REGISTERED
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>
                      ACTIONS
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedCompanies.map((c, index) => {
                    const companyNumber = page * rowsPerPage + index + 1;
                    return (
                      <TableRow
                        key={c.id}
                        hover
                        sx={{
                          transition: "background-color 0.2s",
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        {/* Number / ID */}
                        <TableCell sx={{ color: "text.secondary", fontSize: "0.8125rem", fontWeight: 500 }}>
                          {companyNumber}
                        </TableCell>

                        {/* Company Name & Subdomain */}
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              src={c.logo_url}
                              sx={{
                                width: 36,
                                height: 36,
                                bgcolor: isDark ? "primary.dark" : "primary.light",
                                color: isDark ? "#ffffff" : "primary.main",
                                fontWeight: 700,
                                fontSize: "0.875rem",
                              }}
                            >
                              {c.name ? c.name.charAt(0).toUpperCase() : "C"}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                                {c.name || "Unnamed Company"}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {c.subdomain ? `${c.subdomain}.ebs.com` : `ID: ${c.id}`}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Contact Email */}
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 500 }}>
                              {c.contact_email || "N/A"}
                            </Typography>
                            {c.contact_phone && (
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {c.contact_phone}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>

                        {/* Plan */}
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                              {c.plan_name || "No Plan"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "capitalize" }}>
                              {c.billing_cycle ? `${c.billing_cycle} billing` : "Trial Plan"}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Total Employees */}
                        <TableCell align="center">
                          <Chip
                            icon={<PeopleIcon sx={{ fontSize: "15px !important" }} />}
                            label={
                              <span>
                                <strong>{c.total_employees || 0}</strong>{" "}
                                <span style={{ opacity: 0.75 }}>({c.active_employees || 0} active)</span>
                              </span>
                            }
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              bgcolor: isDark ? "rgba(99, 102, 241, 0.12)" : "#f5f3ff",
                              color: isDark ? "#a5b4fc" : "#4f46e5",
                              border: `1px solid ${isDark ? "rgba(99, 102, 241, 0.25)" : "#ddd6fe"}`,
                              px: 0.5,
                            }}
                          />
                        </TableCell>

                        {/* Status */}
                        <TableCell align="center">
                          {renderStatusChip(c.status)}
                        </TableCell>

                        {/* Created At */}
                        <TableCell sx={{ color: "text.secondary", fontSize: "0.8125rem" }}>
                          {c.created_at ? new Date(c.created_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          }) : "-"}
                        </TableCell>

                        {/* Action */}
                        <TableCell align="center">
                          <Tooltip title="View Company Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(c)}
                              sx={{
                                color: "primary.main",
                                bgcolor: isDark ? "rgba(59, 130, 246, 0.1)" : "#eff6ff",
                                "&:hover": {
                                  bgcolor: isDark ? "rgba(59, 130, 246, 0.2)" : "#dbeafe",
                                },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={companies.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: `1px solid ${isDark ? "#1e293b" : "#f1f5f9"}`,
            }}
          />
        </Paper>
      </motion.div>

      {/* Details & Status Dialog */}
      <Dialog
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: isDark ? "#0f172a" : "#ffffff",
            border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Company Details
          </Typography>
          <IconButton onClick={() => setDetailsModalOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 3 }}>
          {detailsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={30} />
            </Box>
          ) : selectedCompany ? (
            <Stack spacing={2.5}>
              {/* Header Info */}
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={selectedCompany.logo_url}
                  sx={{ width: 56, height: 56, bgcolor: "primary.main", fontSize: "1.25rem", fontWeight: 700 }}
                >
                  {selectedCompany.name?.charAt(0).toUpperCase() || "C"}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {selectedCompany.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Subdomain: <strong>{selectedCompany.subdomain}</strong>
                  </Typography>
                </Box>
              </Stack>

              <Divider />

              {/* Stats Grid */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: "12px",
                      bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Total Employees
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>
                      {companyDetails?.company?.total_employees ?? selectedCompany.total_employees ?? 0}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: "12px",
                      bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Active Employees
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#10b981" }}>
                      {companyDetails?.company?.active_employees ?? selectedCompany.active_employees ?? 0}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: "12px",
                      bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Departments
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {companyDetails?.company?.total_departments ?? selectedCompany.total_departments ?? 0}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: "12px",
                      bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Registered Users
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {companyDetails?.company?.total_users ?? selectedCompany.total_users ?? 1}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Contact & Subscription */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Contact & Subscription
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <EmailIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    <Typography variant="body2">
                      {companyDetails?.company?.contact_email || selectedCompany.contact_email || "N/A"}
                    </Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PlanIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    <Typography variant="body2">
                      Plan: <strong>{companyDetails?.company?.plan_name || selectedCompany.plan_name || "None"}</strong>
                      {selectedCompany.billing_cycle ? ` (${selectedCompany.billing_cycle})` : ""}
                    </Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarTodayIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    <Typography variant="body2">
                      Trial Ends:{" "}
                      {selectedCompany.trial_ends_at
                        ? new Date(selectedCompany.trial_ends_at).toLocaleDateString()
                        : "N/A"}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              <Divider />

              {/* Status Update Control */}
              {/* <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Manage Company Status
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="change-status-label">Company Status</InputLabel>
                    <Select
                      labelId="change-status-label"
                      value={newStatus}
                      label="Company Status"
                      onChange={(e) => setNewStatus(e.target.value)}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="trial">Trial</MenuItem>
                      <MenuItem value="suspended">Suspended</MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    disabled={updatingStatus || newStatus === selectedCompany.status}
                    onClick={handleUpdateStatus}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
                  >
                    {updatingStatus ? "Updating..." : "Save Status"}
                  </Button>
                </Stack>
              </Box> */}
            </Stack>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDetailsModalOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
